/**
 * lifecycle.ts — the action-lifecycle state machine (`PLAN.md` §6.1) and its two live global hard
 * gates (`PLAN.md` §5.2, wired at Suggestion creation by `R3.17b · T11`,
 * `backend-spec.md` §6). `insufficientDataCreatesCollectionTask` and
 * `brandedPromptsCannotProveDiscovery` — the other two gates §5.2 named — were deleted the same
 * task: neither had gained a caller since R0.6 first wrote them (`find_referencing_symbols`
 * confirmed zero production references, only their own unit tests), and no slice through R3.17b
 * plans one. Recorded as a spec gap in `growth-engine-r3.17-known-issues/STATE.md`'s `## Spec
 * gaps` rather than carried indefinitely as dead exports (ARC-37's "done means wired" cuts both
 * ways — an unwired gate is exactly the shape that rule exists to catch).
 *
 * `growth_actions` (`supabase/migrations/20260823181000_growth_actions.sql`) keeps four lifecycle
 * dimensions as separate columns, each backed by a `CHECK` constraint — this module's four state
 * unions mirror those constraints value-for-value. If the two drift, a valid DB row becomes
 * unrepresentable in TypeScript or vice versa, so a state value is never typed by hand twice: each
 * union is derived from its own `as const` array (`(typeof X)[number]`), and `validators.ts`
 * imports those same arrays for its `z.enum()`s rather than retyping them (ARC-18).
 *
 * "Done" never means "worked" (`PLAN.md` §6.1) — collapsing the four dimensions into one status
 * column would make delivered/unverified indistinguishable from delivered/live_verified, which is
 * exactly the distinction the proof ladder (§6.3) exists to preserve. `transitionLifecycle()` is
 * the single entry point (ARC-12's shape) that attempts a transition on exactly one dimension at a
 * time; it is written so that changing one dimension can never, even conditionally, touch another
 * — the property `src/lib/growth/actions/__tests__/lifecycle.test.ts` red-proves.
 *
 * Every legal-transition table is a `Record<State, readonly State[]>` object literal. That is what
 * makes REL-21's exhaustiveness bite: `Record<K, V>` requires every member of `K` as a key, so
 * adding a fifth value to a state union without adding its row to every table referencing that
 * union is a compile error, not a silently-accepted state. See the task report for the scratch
 * edit that confirmed this (added a state, watched `tsc` fail, reverted).
 *
 * Fails closed (REL-03): `transitionLifecycle()` re-validates `target` against its dimension's
 * `as const` array at runtime, even though the TS signature already narrows it — this function may
 * later be called from a Zod-parsed-but-not-narrowed request body (T5's API route), and an
 * unrecognised or illegal target must reject, never fall through to an implicit accept. The same
 * holds for the work dimension's approval fact (`growth-plan-redesign` B5): `in_progress ->
 * delivered` is refused for a row that requires approval, and a missing fact counts as "required".
 */
import type { DetectorActionType } from '@/lib/detectors/types'

// ─── Opportunity ────────────────────────────────────────────────────────────────

export const OPPORTUNITY_STATES = [
  'detected',
  'eligible',
  'suggested',
  'accepted',
  'rejected',
] as const
export type OpportunityState = (typeof OPPORTUNITY_STATES)[number]

/**
 * `detected -> eligible -> suggested -> accepted`, and `suggested -> rejected` (rejection is only
 * reachable from `suggested` — an opportunity must be surfaced before it can be rejected).
 *
 * Two edges widened per `ARCHITECTURE.md` §2 / T2 (`PLAN.md`): `accepted` was previously terminal,
 * which made `evictOutrankedFixes`'s `todo/in_progress -> superseded` write (`generate.ts:387`) an
 * illegal move in this model — a live pipeline hitting a rejection the legacy table never raised.
 * `accepted -> rejected` legalizes system eviction (a higher-ranked candidate displaces this one).
 * `rejected -> accepted` legalizes re-acceptance: a user who rejects a Suggestion can reconsider and
 * accept it later through the modern accept/reject flow (`transitionLifecycle()`'s own caller,
 * `updateGrowthActionLifecycle`). Neither edge widens further: `accepted`/`rejected` still reject
 * every other target, so a fresh `suggested -> accepted -> rejected -> accepted -> ...` cycle is the
 * only new loop this table admits, not a blanket reopening of the state machine.
 *
 * `growth-engine-r3.18-loop-repair` (2026-09-21) update: this paragraph used to cite two OTHER real
 * pipelines as needing the `rejected -> accepted` edge — `accuracy_issues.resolved -> active`
 * reactivation and a fix re-detected after being superseded/dismissed. Both examples are now wrong:
 * the three legacy-mirror adapters (`src/lib/growth/adapters/from-*.ts`) no longer write
 * `opportunityState: 'accepted'` for any status a human never explicitly decided — they write
 * `'detected'` instead, since `'accepted'` had been silently filling the Plan board
 * (`opportunity_state === 'accepted'`) with undecided legacy rows. Re-detection after a legacy
 * rejection (a dismissed fix regenerated, a declined barnacle opportunity reappearing) is therefore
 * now a `rejected -> detected` move, not `rejected -> accepted` — worth noting because none of these
 * writes go through `transitionLifecycle()` at all (`write-action.ts`'s `writeGrowthActions()` sets
 * `opportunity_state` directly from the mapped lifecycle), so this table's legality was never what
 * enforced either example; it only documented the intent. The edge stays legal and still matters for
 * the genuine modern re-acceptance case above.
 */
const OPPORTUNITY_TRANSITIONS: Record<OpportunityState, readonly OpportunityState[]> = {
  detected: ['eligible'],
  eligible: ['suggested'],
  suggested: ['accepted', 'rejected'],
  accepted: ['rejected'],
  rejected: ['accepted'],
}

// ─── Work ───────────────────────────────────────────────────────────────────────

export const WORK_STATES = ['planned', 'in_progress', 'awaiting_approval', 'delivered'] as const
export type WorkState = (typeof WORK_STATES)[number]

/**
 * `planned -> in_progress -> awaiting_approval -> delivered`, and `in_progress -> delivered`
 * ("Mark as done" on an action that does not require approval, `growth-plan-redesign` B5). Also
 * `awaiting_approval -> in_progress` (rejected-at-approval work goes back), no other backward move.
 * `delivered` is terminal.
 *
 * This table says which moves exist; it cannot say which one a given row may take. The
 * `in_progress -> delivered` skip is legal only for a row whose `approval_required` is false — that
 * per-row refusal lives in `transitionLifecycle()` (the work dimension's `WorkTransitionFacts`), so
 * legality stays in the one fail-closed function rather than in each caller.
 */
const WORK_TRANSITIONS: Record<WorkState, readonly WorkState[]> = {
  planned: ['in_progress'],
  in_progress: ['awaiting_approval', 'delivered'],
  awaiting_approval: ['in_progress', 'delivered'],
  delivered: [],
}

/**
 * The per-row fact the work dimension needs beyond its current state: whether this action requires
 * approval before it can be marked done (`growth_actions.approval_required`).
 */
interface WorkTransitionFacts {
  readonly approvalRequired: boolean
}

/** The one next-step button a work state offers, and the state it moves to. */
export interface WorkStep {
  readonly target: WorkState
  readonly label: string
}

/**
 * The one next step a card offers for its work state (`growth-plan-redesign` AC-4) — shared by the
 * Plan card's `NextStepButton` and the Task page's `TaskCard`, so the two can never offer different
 * moves for the same row. Every step returned here is one `transitionLifecycle()` accepts for the
 * same `approvalRequired` (the lifecycle test pins that).
 *
 * - planned → "Start" → in progress;
 * - in progress → "Mark as done" → delivered, or "Send for approval" → awaiting approval when
 *   approval is required;
 * - awaiting approval → "Approve and mark done" → delivered when approval is required, otherwise
 *   "Mark as done" → delivered (a legacy row parked there is never stranded);
 * - delivered → `null`: no next step.
 */
export function nextWorkStep(workState: WorkState, approvalRequired: boolean): WorkStep | null {
  switch (workState) {
    case 'planned':
      return { target: 'in_progress', label: 'Start' }
    case 'in_progress':
      return approvalRequired
        ? { target: 'awaiting_approval', label: 'Send for approval' }
        : { target: 'delivered', label: 'Mark as done' }
    case 'awaiting_approval':
      return approvalRequired
        ? { target: 'delivered', label: 'Approve and mark done' }
        : { target: 'delivered', label: 'Mark as done' }
    case 'delivered':
      return null
    default: {
      const exhaustive: never = workState
      return exhaustive
    }
  }
}

// ─── Delivery ───────────────────────────────────────────────────────────────────

export const DELIVERY_STATES = [
  'unverified',
  'live_verified',
  'not_detected',
  'could_not_verify',
] as const
export type DeliveryState = (typeof DELIVERY_STATES)[number]

/**
 * The three outcomes an actual verify CALL can return — `DeliveryState` minus `'unverified'`,
 * which is only the pre-check default a row starts in, never something a verifier produces
 * (`src/lib/verify/verify-url.ts`, `src/components/shared/delivery-pill.tsx` — both import this
 * rather than redeclaring it; ARC-18. This used to be declared independently, identically, in
 * both of those files — caught during WS · T16 while reusing `DeliveryPill` for the Task detail
 * page's verdict slot).
 */
export type DeliveryVerdict = Exclude<DeliveryState, 'unverified'>

/**
 * `DeliveryVerdict` plus one read-time-derived display state — never a fifth `DeliveryState`
 * member (`STATE.md` `## Decided`, 2026-09-18: "Representation: derived at read time from
 * `work_state='delivered'` + null `verification_method`. No migration, no new `delivery_state`
 * member."). `'not_verifiable'` is `deliveryDisplayState()`'s own output value, not a column value
 * — nothing in this module ever writes it to the DB.
 */
export type DeliveryDisplayState = DeliveryVerdict | 'not_verifiable'

/**
 * The plain-English label for each `DeliveryDisplayState` — the single source `DeliveryPill`
 * (`src/components/shared/delivery-pill.tsx`) and the PDF report renderer
 * (`src/lib/reports/pdf/document.tsx`, reachable from a server-only route handler) both print, so
 * neither can say something different for the same delivery outcome (`growth-engine-r3.17e-p2-
 * sweep` T13, DES-42/ARC-18). Lives here, not in `delivery-pill.tsx`, because that file carries
 * `'use client'`, which makes it unreachable from `document.tsx` (ARC-01) — unlike `DeliveryVerdict`
 * above, whose TYPE alone crosses that boundary fine, a VALUE import does not. Tone
 * (`StatusBadgeTone` for the dashboard pill, `PdfPillTone` for the PDF) stays local to each
 * renderer — the two tone systems are not the same type, and only the label text was the drift
 * risk this constant closes.
 */
export const DELIVERY_STATE_LABEL: Record<DeliveryDisplayState, string> = {
  live_verified: 'Live and verified',
  not_detected: 'Not live yet',
  could_not_verify: 'Could not verify',
  not_verifiable: "Can't be verified automatically",
}

/**
 * The single shared derivation (ARC-18/ARC-19) behind `DeliveryPill`'s `notVerifiable` rendering —
 * used by both the Task card (`task-card.tsx`) and the action Report page
 * (`actions/[id]/report/page.tsx`) so the two surfaces can never disagree about which of three
 * situations a delivered-or-earlier action is in (R3.17a T6, ISSUE-3).
 *
 * `hasRecipe` is the caller-supplied signal — `growth_actions.verification_method !== null` — that
 * `verify-growth-action.ts`'s null-recipe branch (T5) needs to disambiguate the *only* case this
 * function can't tell apart from `deliveryState` alone: a delivered action whose
 * `deliveryState` is still `'unverified'`. That single value is structurally ambiguous between "a
 * check has not run yet" (has a recipe, will be checked) and "nothing will ever check this" (no
 * recipe — T5's `no_recipe` outcome never writes a lifecycle transition, by design, since it is not
 * a pending check). `hasRecipe` resolves that ambiguity; `workState` scopes the whole derivation to
 * after delivery, per `DeliveryPill`'s own "never render before delivery" contract (PRD-08).
 *
 * - `deliveryState !== 'unverified'` — a verify check has actually run. Returns it unchanged,
 *   ignoring `hasRecipe`/`workState` entirely, so every existing caller keeps its exact current
 *   behaviour (the `## Reuse`/`## Callers` tables' "no — the union is not widened" rows).
 * - `deliveryState === 'unverified'`, `workState === 'delivered'`, no recipe — `'not_verifiable'`:
 *   this action will never be checked; name that honestly instead of rendering as though a check
 *   just hasn't run yet.
 * - `deliveryState === 'unverified'`, `workState === 'delivered'`, has a recipe — `null` (no pill):
 *   a check has not run yet. Matches `DeliveryPill`'s existing "omit rather than a pending
 *   placeholder" contract (PRD-08).
 * - not yet delivered — always `null`, regardless of recipe presence; the whole state only applies
 *   once work has shipped.
 */
export function deliveryDisplayState(
  workState: WorkState,
  deliveryState: DeliveryState,
  hasRecipe: boolean
): DeliveryDisplayState | null {
  if (deliveryState !== 'unverified') {
    return deliveryState
  }
  if (workState === 'delivered' && !hasRecipe) {
    return 'not_verifiable'
  }
  return null
}

/**
 * Whether an action has a Report page worth linking to: only once its change is confirmed live
 * (`live_verified`). The one rule behind the Task card's "View report" link (`task-card.tsx`) and
 * the Plan's Done rows and Done hint (`growth-plan-redesign` B3), so no two surfaces can disagree
 * about which actions have a report.
 */
export function hasReport(deliveryState: DeliveryState): boolean {
  return deliveryState === 'live_verified'
}

/**
 * `unverified -> live_verified | not_detected | could_not_verify`. `not_detected ->
 * live_verified` and `could_not_verify -> live_verified` are also legal (a re-check can later
 * confirm delivery an earlier check missed or couldn't reach).
 *
 * `live_verified -> not_detected` **and** `live_verified -> live_verified` are ALSO legal
 * (`growth-engine-r3.9-focus-verdict · T23`, `backend-spec.md` §5.2) — this reverses what this
 * docblock used to say ("once confirmed live, it does not un-confirm... there is no `live_verified
 * -> live_verified` self-loop"). Both halves of that claim were true only because nothing ever
 * re-checked a `live_verified` row a second time; `src/trigger/reverify-live-actions.ts` is exactly
 * that missing caller, and it re-checks routinely, not exceptionally. A widened table with the old
 * docblock left in place would be worse than either alone: the next reader would trust the prose and
 * misread the code (`backend-spec.md` §5.2's own words).
 *
 * The self-loop is not cosmetic — its absence was a real defect, not a harmless one. Every caller of
 * `transitionLifecycle` that gets `{ok: false}` back treats it as an ILLEGAL transition:
 * `verify-growth-action.ts`'s own handling (`:182-190`) Sentry-captures an exception and returns
 * `{ok: false}` from the task. Before this fix, the single most common outcome of a re-verification —
 * "still live, nothing changed" — took that exact path: a healthy, routine confirmation reported as a
 * failure and captured as an error on every occurrence. That is REL-06's shape precisely (a capability
 * whose ordinary success path looks like failure), so `live_verified -> live_verified` is legalized
 * as what it actually is: a re-verification confirming the existing state, not a no-op outside the
 * state machine and not an error. `verify-growth-action.ts`'s error handling is intentionally left
 * alone (it is still correct for the two genuinely illegal targets, `unverified` and
 * `could_not_verify`, below) — the fix belongs in the table that was wrong, not in the caller that
 * correctly trusted it.
 *
 * The write that takes the `not_detected` transition always pairs `deliveryState: 'not_detected'`
 * with `growth_actions.reversed_at`/`reversal_reason` (T11's migration, `6e3a3870`) so the Focus's
 * window can stop counting the Action's contribution from that date (§4.7) — enforced by the caller
 * (`reverify-live-actions.ts`), not by a CHECK on this table: a CHECK would duplicate this transition
 * table and drift from it the first time the table changes without the constraint changing too (see
 * that migration's own comment). The `live_verified -> live_verified` transition writes nothing beyond
 * `last_reverified_at` (also the caller's job, not this table's).
 *
 * No other target is legal from `live_verified` — `unverified` and `could_not_verify` remain rejected,
 * so a genuine illegal transition still fails the way it always has. A later Action that gets
 * re-verified live again after a reversal re-enters at `not_detected -> live_verified`, already legal
 * above.
 *
 * `not_detected` and `could_not_verify` may now each move to `live_verified`, `not_detected` and
 * `could_not_verify` — themselves included (R6.8 B2, AC-1, critique G1). A daily re-check must be able
 * to record what it found: a Task still failing rewrites its per-page evidence (a same-state write
 * stamps no `verified_at` and no `reversed_at`), a site that stops answering reads "Could not verify",
 * and one that answers again reads "Not live yet" or "Live and verified". Before this, those re-checks
 * were refused and the card kept showing the first run's evidence. The one-way door stays on
 * `live_verified`: a live row never moves to `could_not_verify` (a failed look is not a reversal), and
 * nothing moves back to `unverified`.
 *
 * One row fact this table cannot see: a `not_detected` row that was once live (`ever_live_verified`)
 * with no `reversed_at` is a reversal `reverify-live-actions` has yet to stamp from exactly that
 * state, so moving it to `could_not_verify` would lose the reversal. The table has no row facts, so
 * the writer enforces it: `verify-growth-action.ts` (`isPendingReversal`) leaves such a row
 * `not_detected` when a check cannot run (code-review E2). It is every caller's job, not this
 * table's, to keep that row where it is.
 */
const DELIVERY_TRANSITIONS: Record<DeliveryState, readonly DeliveryState[]> = {
  unverified: ['live_verified', 'not_detected', 'could_not_verify'],
  live_verified: ['not_detected', 'live_verified'],
  not_detected: ['live_verified', 'not_detected', 'could_not_verify'],
  could_not_verify: ['live_verified', 'not_detected', 'could_not_verify'],
}

// ─── Measurement ────────────────────────────────────────────────────────────────

export const MEASUREMENT_STATES = ['waiting', 'observing', 'ready', 'complete'] as const
export type MeasurementState = (typeof MEASUREMENT_STATES)[number]

/**
 * `waiting -> observing -> ready -> complete`. No backward transitions, no skips. `complete` is
 * terminal.
 */
const MEASUREMENT_TRANSITIONS: Record<MeasurementState, readonly MeasurementState[]> = {
  waiting: ['observing'],
  observing: ['ready'],
  ready: ['complete'],
  complete: [],
}

// ─── Resolution reason (T1 — closes the three lifecycle collapses) ─────────────

/**
 * `ARCHITECTURE.md` §2 identified three legacy-status pairs that collapse onto an identical
 * 4-dimension tuple even though they mean different things: `accuracy_issues.active` vs
 * `.resolved` (still reproducing vs last re-check found it gone), `fixes.dismissed` vs
 * `.superseded` (user rejected vs system-evicted by a higher-ranked candidate), and
 * `barnacle_opportunities.declined` vs either of those two (a third, structurally distinct
 * user-rejection path). None of the four existing dimensions has room for these distinctions
 * without overloading a value's meaning (`measurementState: 'observing'` already means "still
 * being re-checked", not "still reproducing"; `opportunityState: 'rejected'` already means "not
 * being pursued", not "who/what rejected it").
 *
 * This is deliberately a compensating column, not a 5th transitioned dimension
 * (`ARCHITECTURE.md`'s "Four dimensions, plus the compensating columns §2 requires"):
 * `transitionLifecycle()` never reads or writes it — it rides along unchanged on every `{
 * ...current }` spread in `transitionLifecycle`'s switch, exactly like every other field this
 * function doesn't touch. It exists purely so the three adapters can produce distinct, round-
 * trippable tuples per legacy status; it is not itself a state machine.
 *
 * Optional (not every `ActionLifecycleState` literal in the codebase sets it — most legacy
 * statuses have nothing to say here, and a live `growth_actions` row created directly through
 * T5's route has no legacy status to disambiguate from). Adapters that need to disambiguate a
 * specific pair set an explicit reason; every other case sets it explicitly to `null` for clarity,
 * per each adapter's own table.
 */
export const RESOLUTION_REASONS = [
  'still_reproducing',
  'confirmed_resolved',
  'user_dismissed',
  'system_superseded',
  'user_declined',
] as const
export type ResolutionReason = (typeof RESOLUTION_REASONS)[number]

// ─── Combined state + entry point ──────────────────────────────────────────────

export interface ActionLifecycleState {
  readonly opportunityState: OpportunityState
  readonly workState: WorkState
  readonly deliveryState: DeliveryState
  readonly measurementState: MeasurementState
  /** See `RESOLUTION_REASONS` docblock above. Optional — most callers have nothing to say here. */
  readonly resolutionReason?: ResolutionReason | null
}

export const LIFECYCLE_DIMENSIONS = ['opportunity', 'work', 'delivery', 'measurement'] as const
export type LifecycleDimension = (typeof LIFECYCLE_DIMENSIONS)[number]

export type LifecycleTransitionTarget =
  | OpportunityState
  | WorkState
  | DeliveryState
  | MeasurementState

export type LifecycleTransitionResult =
  | { readonly ok: true; readonly state: ActionLifecycleState }
  | { readonly ok: false; readonly reason: string }

/** Narrows `value` to `S` only if it is a member of `validStates` — the REL-03 runtime guard. */
function isValidState<S extends string>(value: string, validStates: readonly S[]): value is S {
  return (validStates as readonly string[]).includes(value)
}

/**
 * Shared per-dimension check: is `target` a recognised value for this dimension, and is the move
 * from `current` to `target` in the legal-transitions table? Fails closed on both questions —
 * never falls through to an implicit accept.
 */
function attemptTransition<S extends string>(
  current: S,
  target: string,
  validStates: readonly S[],
  transitions: Record<S, readonly S[]>
): { readonly ok: true; readonly next: S } | { readonly ok: false; readonly reason: string } {
  if (!isValidState(target, validStates)) {
    return { ok: false, reason: `"${target}" is not a recognised state for this dimension` }
  }
  const legalTargets = transitions[current]
  if (!legalTargets.includes(target)) {
    return { ok: false, reason: `illegal transition from "${current}" to "${target}"` }
  }
  return { ok: true, next: target }
}

/**
 * The single entry point (ARC-12's shape) for attempting a lifecycle transition. Exactly one
 * dimension moves per call — the other three dimensions on the returned state are byte-for-byte
 * identical to `current`, never touched even conditionally. No other function may mutate an
 * `ActionLifecycleState`'s dimension fields.
 *
 * The work dimension also takes the row's `WorkTransitionFacts` (`growth-plan-redesign` B5) and
 * refuses `in_progress -> delivered` when approval is required — before any caller writes. The other
 * three dimensions take none. A work call that arrives without the fact at runtime (an untyped
 * caller) is treated as approval required: fails closed (REL-03), never skips approval.
 */
export function transitionLifecycle(
  current: ActionLifecycleState,
  dimension: 'work',
  target: LifecycleTransitionTarget,
  work: WorkTransitionFacts
): LifecycleTransitionResult
export function transitionLifecycle(
  current: ActionLifecycleState,
  dimension: Exclude<LifecycleDimension, 'work'>,
  target: LifecycleTransitionTarget
): LifecycleTransitionResult
export function transitionLifecycle(
  current: ActionLifecycleState,
  dimension: LifecycleDimension,
  target: LifecycleTransitionTarget,
  work?: WorkTransitionFacts
): LifecycleTransitionResult {
  switch (dimension) {
    case 'opportunity': {
      const result = attemptTransition(
        current.opportunityState,
        target,
        OPPORTUNITY_STATES,
        OPPORTUNITY_TRANSITIONS
      )
      if (!result.ok) return result
      return { ok: true, state: { ...current, opportunityState: result.next } }
    }
    case 'work': {
      const result = attemptTransition(current.workState, target, WORK_STATES, WORK_TRANSITIONS)
      if (!result.ok) return result
      const approvalRequired = work?.approvalRequired ?? true
      if (current.workState === 'in_progress' && result.next === 'delivered' && approvalRequired) {
        return {
          ok: false,
          reason: 'This action requires approval before it can be marked done',
        }
      }
      return { ok: true, state: { ...current, workState: result.next } }
    }
    case 'delivery': {
      const result = attemptTransition(
        current.deliveryState,
        target,
        DELIVERY_STATES,
        DELIVERY_TRANSITIONS
      )
      if (!result.ok) return result
      return { ok: true, state: { ...current, deliveryState: result.next } }
    }
    case 'measurement': {
      const result = attemptTransition(
        current.measurementState,
        target,
        MEASUREMENT_STATES,
        MEASUREMENT_TRANSITIONS
      )
      if (!result.ok) return result
      return { ok: true, state: { ...current, measurementState: result.next } }
    }
    default: {
      // REL-21: a fifth `LifecycleDimension` value fails to compile here until this switch is
      // updated — the assignment to `never` is the enumeration check, not dead code.
      const exhaustive: never = dimension
      return { ok: false, reason: `unrecognised dimension "${String(exhaustive)}"` }
    }
  }
}

// ─── Hard gates (PLAN.md §5.2, wired R3.17b · T11) ──────────────────────────────

/**
 * `backend-spec.md` §6 — the read-only context both gates below need, assembled by the caller
 * (`assess-scope-state.ts` batches this once per project; `propose-suggestion/route.ts` builds a
 * one-scope version) and threaded through `createSuggestionFromFinding()`.
 *
 * `scopeState` is widened past the five real `ScopeState` members (`src/types/growth-state.ts`) to
 * `| string` deliberately — this module only ever compares it against the single literal
 * `'recover'`, so it takes the widened type rather than importing the full enum, the same
 * "structural, not nominal" choice `LifecycleTransitionTarget` already makes for its own state
 * unions. `null` is a scope with no persisted `growth_scope_state_current` row yet (never gated —
 * see `recoverySuppressesOrdinaryGrowth`).
 */
export type SuggestionGateContext = {
  readonly scopeState: 'build' | 'maintain' | 'recover' | string | null
  readonly eligibilityBlock: { readonly actionId: string; readonly title: string } | null
  readonly eligibilityReadFailed?: boolean
}

/**
 * `backend-spec.md` §7 — every hard-gate suppression reason is written to `growth_actions
 * .suppression_reason` prefixed with a stable kind token so `getHeldBackSuggestionCounts` (T12)
 * can group by kind without parsing free text; the prefix is stripped back off wherever the reason
 * is shown to a user (`parseSuppressionReason`, below). One format function per direction keeps
 * the token spelling in exactly one place (ARC-18) — neither gate below builds the string by hand.
 */
export type SuggestionSuppressionKind = 'recovery' | 'eligibility'

/** The start of an eligibility hold that names an open blocker. The blocker's title follows it;
 *  rows written before R6.12 carry the blocker's raw finding there instead, so a reader that shows
 *  the hold maps everything after this prefix to the title (`getHeldBackSuggestionCounts`). */
export const ELIGIBILITY_BLOCK_MESSAGE_PREFIX = 'blocked by an open eligibility issue: '

export function formatSuppressionReason(kind: SuggestionSuppressionKind, message: string): string {
  return `${kind}: ${message}`
}

/** The inverse of {@link formatSuppressionReason} — `null` when `reason` does not carry a
 * recognised kind token (a legacy or hand-written value). */
export function parseSuppressionReason(
  reason: string
): { readonly kind: SuggestionSuppressionKind; readonly message: string } | null {
  const match = /^(recovery|eligibility):\s*([\s\S]*)$/u.exec(reason)
  if (!match) return null
  const kind = match[1]
  const message = match[2]
  if ((kind !== 'recovery' && kind !== 'eligibility') || message === undefined) return null
  return { kind, message }
}

/** Ordinary growth per `backend-spec.md` §6 — everything else in `DetectorActionType` is repair
 * work, which recovery never suppresses. */
const ORDINARY_GROWTH_ACTION_TYPES: readonly DetectorActionType[] = ['create', 'distribute']

/**
 * `PLAN.md` §5.2 / `backend-spec.md` §6 — "Recovery suppresses ordinary growth." `scopeState` is
 * the scope's PREVIOUS `growth_scope_state_current.displayed_state` (never this run's own —
 * `assess-scope-state.ts`'s own docblock explains why gating on this run's freshly-computed state
 * would be circular). Fires only for `create`/`distribute` findings while that state is
 * `'recover'`; every other scope state and every repair `actionType` passes through unsuppressed,
 * including a scope with no persisted state yet (`scopeState: null` — a first assessment is never
 * gated).
 */
export function recoverySuppressesOrdinaryGrowth(
  scopeState: SuggestionGateContext['scopeState'],
  actionType: DetectorActionType
): { readonly suppressed: boolean; readonly reason?: string } {
  if (scopeState !== 'recover') return { suppressed: false }
  if (!ORDINARY_GROWTH_ACTION_TYPES.includes(actionType)) return { suppressed: false }
  return {
    suppressed: true,
    reason: formatSuppressionReason(
      'recovery',
      'held back while this part of your site recovers: repair work comes first'
    ),
  }
}

/**
 * `PLAN.md` §5.2 / `backend-spec.md` §6 — "blocked eligibility suppresses distribution." Only
 * `actionType === 'distribute'` is ever in scope — every other action type passes through
 * regardless of `ctx`. `ctx.eligibilityReadFailed` fails CLOSED (REL-03): a batch that could not
 * confirm eligibility this run must not let a `distribute` finding through on the assumption
 * eligibility was fine. Otherwise suppressed exactly when `ctx.eligibilityBlock` names an open
 * `technical-eligibility` action still blocking this scope.
 */
export function blockedEligibilitySuppressesDistribution(
  ctx: SuggestionGateContext,
  actionType: DetectorActionType
): { readonly suppressed: boolean; readonly reason?: string } {
  if (actionType !== 'distribute') return { suppressed: false }
  if (ctx.eligibilityReadFailed) {
    return {
      suppressed: true,
      reason: formatSuppressionReason('eligibility', "couldn't confirm eligibility"),
    }
  }
  if (ctx.eligibilityBlock) {
    return {
      suppressed: true,
      reason: formatSuppressionReason(
        'eligibility',
        `${ELIGIBILITY_BLOCK_MESSAGE_PREFIX}${ctx.eligibilityBlock.title}`
      ),
    }
  }
  return { suppressed: false }
}
